/**
 * Enova Wishlist System
 * Simple localStorage-based wishlist functionality
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'enova_wishlist_products';

  /**
   * Wishlist Manager
   */
  class EnovaWishlist {
    constructor() {
      this.products = this.getProducts();
      this.init();
    }

    init() {
      this.updateAllButtons();
      this.updateHeaderIcon();
      this.updatePageCount();
      this.setupEventListeners();
      
      // Escuchar cambios de localStorage entre pestañas
      window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY) {
          this.products = this.getProducts();
          this.updateAllButtons();
          this.updateHeaderIcon();
          this.updatePageCount();
          this.renderWishlistPage();
        }
      });

      // Si estamos en la página de wishlist, renderizarla
      if (document.querySelector('[data-enova-wishlist-page]')) {
        this.renderWishlistPage();
      }

      console.log('[Enova Wishlist] Initialized with', this.products.length, 'products');
    }

    /**
     * Obtener productos del localStorage
     */
    getProducts() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error('[Enova Wishlist] Error reading localStorage:', error);
        return [];
      }
    }

    /**
     * Guardar productos en localStorage
     */
    saveProducts() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.products));
        console.log('[Enova Wishlist] Saved', this.products.length, 'products');
      } catch (error) {
        console.error('[Enova Wishlist] Error saving to localStorage:', error);
      }
    }

    /**
     * Verificar si un producto está en wishlist
     */
    isInWishlist(productId) {
      return this.products.some(p => p.id === productId);
    }

    /**
     * Agregar producto a wishlist
     */
    addProduct(productData) {
      if (this.isInWishlist(productData.id)) {
        console.log('[Enova Wishlist] Product already in wishlist:', productData.id);
        return false;
      }

      this.products.push({
        id: productData.id,
        handle: productData.handle,
        title: productData.title,
        image: productData.image,
        price: productData.price,
        compareAtPrice: productData.compareAtPrice,
        url: productData.url,
        addedAt: new Date().toISOString()
      });

      this.saveProducts();
      this.updateAllButtons();
      this.updateHeaderIcon();
      this.updatePageCount();
      
      console.log('[Enova Wishlist] Product added:', productData.title);
      return true;
    }

    /**
     * Remover producto de wishlist
     */
    removeProduct(productId) {
      const initialLength = this.products.length;
      this.products = this.products.filter(p => p.id !== productId);
      
      if (this.products.length < initialLength) {
        this.saveProducts();
        this.updateAllButtons();
        this.updateHeaderIcon();
        this.updatePageCount();
        this.renderWishlistPage();
        
        console.log('[Enova Wishlist] Product removed:', productId);
        return true;
      }
      
      return false;
    }

    /**
     * Toggle producto (agregar o remover)
     */
    toggleProduct(productData) {
      if (this.isInWishlist(productData.id)) {
        return this.removeProduct(productData.id);
      } else {
        return this.addProduct(productData);
      }
    }

    /**
     * Limpiar wishlist
     */
    clearWishlist() {
      this.products = [];
      this.saveProducts();
      this.updateAllButtons();
      this.updateHeaderIcon();
      this.updatePageCount();
      this.renderWishlistPage();
      
      console.log('[Enova Wishlist] Cleared');
    }

    /**
     * Actualizar todos los botones de wishlist
     */
    updateAllButtons() {
      document.querySelectorAll('[data-enova-wishlist-btn]').forEach(btn => {
        const productId = btn.getAttribute('data-product-id');
        this.updateButton(btn, this.isInWishlist(productId));
      });
    }

    /**
     * Actualizar contador de productos en la página
     */
    updatePageCount() {
      const count = this.products.length;
      
      // Actualizar el contador numérico
      const countElements = document.querySelectorAll('[data-wishlist-count]');
      countElements.forEach(el => {
        el.textContent = count;
      });

      // Actualizar el texto (singular/plural)
      const textElements = document.querySelectorAll('[data-wishlist-count-text]');
      textElements.forEach(el => {
        el.textContent = count === 1 ? 'product' : 'products';
      });
    }

    /**
     * Actualizar un botón específico
     */
    updateButton(button, isInWishlist) {
      if (isInWishlist) {
        button.classList.add('is-active');
        button.setAttribute('aria-label', 'Remove from wishlist');
        const icon = button.querySelector('.enova-wishlist-icon');
        if (icon) {
          icon.innerHTML = this.getHeartIconFilled();
        }
      } else {
        button.classList.remove('is-active');
        button.setAttribute('aria-label', 'Add to wishlist');
        const icon = button.querySelector('.enova-wishlist-icon');
        if (icon) {
          icon.innerHTML = this.getHeartIconOutline();
        }
      }
    }

    /**
     * Actualizar icono del header
     */
    updateHeaderIcon() {
      const headerIcon = document.querySelector('[data-enova-wishlist-header]');
      if (!headerIcon) return;

      const badge = headerIcon.querySelector('.cart-count-bubble');
      const count = this.products.length;

      if (badge) {
        const span = badge.querySelector('span[aria-hidden="true"]');
        if (span) {
          span.textContent = count;
        }
        
        if (count > 0) {
          badge.style.display = 'flex';
        } else {
          badge.style.display = 'none';
        }
      }
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
      // Delegar eventos a los botones de wishlist
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-enova-wishlist-btn]');
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();

        const productData = {
          id: btn.getAttribute('data-product-id'),
          handle: btn.getAttribute('data-product-handle'),
          title: btn.getAttribute('data-product-title'),
          image: btn.getAttribute('data-product-image'),
          price: btn.getAttribute('data-product-price'),
          compareAtPrice: btn.getAttribute('data-product-compare-price'),
          url: btn.getAttribute('data-product-url')
        };

        this.toggleProduct(productData);
        
        // Animación de feedback
        btn.classList.add('enova-wishlist-pulse');
        setTimeout(() => btn.classList.remove('enova-wishlist-pulse'), 600);
      });

      // Botones de remover en la página de wishlist
      document.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-enova-wishlist-remove]');
        if (!removeBtn) return;

        e.preventDefault();
        const productId = removeBtn.getAttribute('data-product-id');
        this.removeProduct(productId);
      });
    }

    /**
     * Renderizar página de wishlist
     */
    async renderWishlistPage() {
      const container = document.querySelector('[data-enova-wishlist-page]');
      if (!container) return;

      const grid = container.querySelector('[data-enova-wishlist-grid]');
      const emptyState = container.querySelector('[data-enova-wishlist-empty]');

      if (this.products.length === 0) {
        if (grid) grid.style.display = 'none';
        if (emptyState) emptyState.style.display = 'block';
        return;
      }

      if (grid) grid.style.display = 'grid';
      if (emptyState) emptyState.style.display = 'none';

      if (grid) {
        grid.innerHTML = this.products.map(product => this.renderProductCard(product)).join('');
      }
    }

    /**
     * Renderizar tarjeta de producto
     */
    renderProductCard(product) {
      const priceHTML = product.compareAtPrice && product.compareAtPrice !== product.price
        ? `
          <div class="price__container">
            <div class="price__regular">
              <s class="price-item price-item--regular">${product.compareAtPrice}</s>
            </div>
            <div class="price__sale">
              <span class="price-item price-item--sale">${product.price}</span>
            </div>
          </div>
        `
        : `<span class="price-item">${product.price}</span>`;

      return `
        <div class="enova-wishlist-card" data-product-id="${product.id}">
          <div class="enova-wishlist-card__image-wrapper">
            <a href="${product.url}">
              <img 
                src="${product.image}" 
                alt="${product.title}"
                class="enova-wishlist-card__image"
                loading="lazy"
              />
            </a>
            <button 
              class="enova-wishlist-card__remove"
              data-enova-wishlist-remove
              data-product-id="${product.id}"
              aria-label="Remove from wishlist"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="enova-wishlist-card__info">
            <a href="${product.url}" class="enova-wishlist-card__title">
              ${product.title}
            </a>
            <div class="enova-wishlist-card__price price">
              ${priceHTML}
            </div>
          </div>
        </div>
      `;
    }

    /**
     * Iconos SVG
     */
    getHeartIconOutline() {
      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
      `;
    }

    getHeartIconFilled() {
      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
      `;
    }
  }

  // Inicializar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.EnovaWishlist = new EnovaWishlist();
    });
  } else {
    window.EnovaWishlist = new EnovaWishlist();
  }

})();

